import { useMemo } from 'react';

import type { Ticket } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import type { ColumnInstance } from 'react-table';

import { DateTime } from '@packages/components/dateTime/DateTime';
import { TicketStatusBadge } from '@packages/components/ticket-status-badge/ticket-status-badge';

import { LogoBadge } from '../../logo-badge/logo-badge';
import { TicketType } from '../../ticket/ticket-type';
import { CcAgentCell } from '../../ticketList/cc-agent-cell/cc-agent-cell';

import { TooltipForText } from './tooltip-for-text/tooltip-for-text';
import { ViewTicketLink } from './view-ticket-link';

type CellProps = {
  row: {
    original: Ticket;
  };
};

type ValueObj = {
  value: string;
};

export const useTicketListColumns = (): ColumnInstance[] => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        Cell: ({ value }: ValueObj) => {
          return <ViewTicketLink ticketId={value} />;
        },
        Header: t('ticket:ticketId'),
        accessor: 'id',
        id: 'id',
        sortable: false,
        width: '5',
      },
      {
        Cell: ({ value }: ValueObj) => <TooltipForText value={value} />,
        Header: t('ticket:ticketSubject'),
        accessor: 'subject',
        id: 'subject',
        sortable: true,
      },
      {
        Cell: ({ value }: ValueObj) => <TicketStatusBadge value={value} />,
        Header: t('ticket:details.status'),
        accessor: 'status',
        id: 'status',
        sortable: true,
        width: '10',
      },
      {
        Cell: ({ value }: ValueObj) => <TicketType value={value} />,
        Header: t('ticket:details.ticketType'),
        accessor: 'type',
        id: 'type',
        sortable: true,
        width: '10',
      },
      {
        Cell: ({ row }: CellProps) => (
          <LogoBadge
            text={row.original.requester}
            showLogo={row.original.requesterAgent}
          />
        ),
        Header: t('ticket:details.to'),
        accessor: 'requester',
        id: 'requester',
        sortable: true,
        width: '10',
      },
      {
        Cell: ({ row }: CellProps) => (
          <CcAgentCell value={row.original.ccAgent} />
        ),
        Header: t('ticket:details.cc'),
        accessor: 'ccAgent',
        id: 'ccAgent',
        width: '10',
      },
      {
        Cell: ({ value }: ValueObj) => <DateTime value={value} />,
        Header: t('ticket:details.created'),
        accessor: 'createdAt',
        id: 'createdAt',
        sortable: true,
        width: '7',
      },
      {
        Cell: ({ value }: ValueObj) => <DateTime value={value} />,
        Header: t('ticket:details.lastUpdated'),
        accessor: 'updatedAt',
        id: 'updatedAt',
        sortable: true,
        width: '10',
      },
    ],
    [t],
  );

  return columns as unknown as ColumnInstance[];
};
